import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import * as Util from "../utils/util.js";
import { storage } from "../utils/storage.js";
import { handleAndShowError, showError } from "../utils/errorHandler.js";
import { ERROR_MESSAGES } from "../constants/constants.js";
import api from "../api/client.js";
import { ENDPOINTS } from "../api/endpoints.js";
import { getTenantColors } from "../styles/utils/colors";
import { getCurrentTenant } from "../config/tenants.js";
import "../components/Tabs.css";

const createCssTextField = (colors) => styled(TextField)({
  "& label.Mui-focused": {
    color: colors.loginTextFieldFocus,
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: colors.loginTextFieldFocus,
  },
  "& .MuiOutlinedInput-root": {
    height: "auto !important",
    minHeight: "40px",
    fontSize: "15px !important",
    fontFamily: "inherit",
    "& fieldset": {
      borderColor: "rgba(0, 0, 0, 0.23)",
    },
    "&.Mui-focused fieldset": {
      borderColor: colors.loginTextFieldFocus,
    },
  },
  "& .MuiOutlinedInput-input": {
    fontSize: "15px !important",
    padding: "16.5px 14px",
  },
  "& .MuiInputLabel-root": {
    fontSize: "15px",
    transform: "translate(14px, 12px) scale(1)",
    "&.MuiInputLabel-shrink": {
      transform: "translate(14px, -9px) scale(0.75)",
    },
  },
});

const useStyles = (colors) => ({
  textField_ID: {
    marginTop: "16px",
    marginBottom: "8px",
  },
  textField_PW: {
    marginBottom: "8px",
  },
  button: {
    marginTop: "16px",
    marginLeft: "0px !important",
    width: "100%",
    height: "45px",
    backgroundColor: colors.loginButton,
    color: "#ffffff",
    fontSize: "1rem !important",
    fontWeight: "400 !important",
    textTransform: "none !important",
    "&:focus": {
      backgroundColor: colors.loginButton,
    },
    "&:hover": {
      backgroundColor: colors.loginButton,
    },
  },
  backdrop: {
    zIndex: 1252,
    color: "#fff",
  },
});

function Login(props) {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState({
    ID: "",
    PW: "",
  });
  const [regOption, setRegOption] = useState("2"); // 기본값: 업체용
  
  // 테넌트 정보 (기본값)
  const [tenant, setTenant] = useState(getCurrentTenant());
  
  // 테넌트별 색상 상태
  const [tenantColors, setTenantColors] = useState(getTenantColors());
  
  // 동적 styled component 생성
  const CssTextField = React.useMemo(
    () => createCssTextField(tenantColors),
    [tenantColors]
  );
  
  const classes = useStyles(tenantColors);

  const { ID, PW } = inputs;
  const [loading, setLoading] = React.useState(false);

  const handleChange = (e) => {
    const { value, name } = e.target;
    setInputs({
      ...inputs,
      [name]: value,
    });
  };

  useEffect(() => {
    window.sessionStorage.clear();
  }, []);

  const signIn = (user_id, pass) => {
    if (loading) {
      return;
    }
    setLoading(true);
    let form = new FormData();
    form.append("id", user_id);
    form.append("pass", pass);
    form.append("regOption", regOption);

    if (process.env.NODE_ENV === "development") {
    console.log("Login attempt for user:", user_id);
    }

    api
      .post(ENDPOINTS.LOGIN, form, {
        skipErrorHandler: true,
      })
      .then((responseData) => {
        if (responseData.code === "0") {
          if (responseData.PROGRAM.length === 0) {
            showError(ERROR_MESSAGES.NO_PROGRAM_PERMISSION, "warning");
            setLoading(false);
          } else {
            // 테넌트 정보 확인 (백엔드 응답 우선, 없으면 기본값)
            if (responseData.TENANT_INFO) {
              const tenantInfo = {
                companyName: responseData.TENANT_INFO.COMPANY_NAME || tenant.companyName,
                logoPath: responseData.TENANT_INFO.COMPANY_LOGO ? `./images/${responseData.TENANT_INFO.COMPANY_LOGO}` : tenant.logoPath,
              };
              setTenant(tenantInfo);
              storage.set("tenant_info", tenantInfo);
              
              // 테넌트별 색상 업데이트 (도메인 기반)
              const updatedColors = getTenantColors();
              setTenantColors(updatedColors);
              storage.set("tenant_colors", updatedColors);
            }
            
            // 세션 스토리지에 로그인 정보 저장
            storage.setUserId(responseData.DATA.USER_ID);
            storage.setUserName(responseData.DATA.USER_NAME);
            storage.setWorkplaceCd(responseData.DATA.CUST_CD);
            storage.setWorkplaceNm(responseData.DATA.CUST_NAME);
            storage.setTelNo(responseData.DATA.TEL_NO);
            storage.setEmail(responseData.DATA.E_MAIL);
            storage.setSellCustCd(responseData.DATA_CUSTOM);
            storage.setCodeNm(responseData.CODE_NM);
            storage.setCodeStan(responseData.CODE_STAN);
            storage.setYardCustCd(responseData.CODE_YARD_CUST_CD || []);
            storage.setNameCd(responseData.CODE_NM || []);
            storage.setTypeCd(responseData.CODE_TYPE_CD || []);
            storage.setStanCd(responseData.CODE_STAN || []);
            storage.setSellCustCd(responseData.CODE_SELL_CUST_CD || []);
            storage.setBuyCustCd(responseData.CODE_BUY_CUST_CD || []);
            storage.setSalesManCd(responseData.CODE_SALES_MAN_CD || []);
            storage.setJobCustCd(responseData.CODE_JOB_CUST_CD || []);
            storage.setProcPlantCd(responseData.CODE_PLANT_CD || []);
            storage.setProgram(responseData.PROGRAM);
            // NOTE: 서버에서 반환한 REG_OPTION 값을 사용 (로컬 state가 아닌)
            const serverRegOption = responseData.REG_OPTION || regOption;
            storage.set("regOption", serverRegOption); // 자사용/업체용 구분값 저장
            if (process.env.NODE_ENV === "development") {
              console.log("Login response:", responseData);
              console.log("PROGRAM data:", responseData.PROGRAM);
              console.log("PROGRAM length:", responseData.PROGRAM?.length);
            }
            navigate("/home", {
              replace: true,
              state: { program: responseData.PROGRAM },
            });
          }
        } else {
          setLoading(false);
          showError(responseData.RESULT_MESSAGE, "error");
        }
      })
      .catch((e) => {
        setLoading(false);
        handleAndShowError(e, "로그인 중 오류가 발생했습니다.");
      });
  };

  return (
    <Box className="tab-root">
      <Box
        sx={{
          width: "100%",
          height: "100%",
          padding: "6px 8px",
          boxSizing: "border-box",
          backgroundColor: tenantColors.loginBackground,
          borderRadius: "14px",
        }}
      >
        <Backdrop sx={classes.backdrop} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            justifyContent: "center",
            textAlign: "center",
            color: tenantColors.loginText,
          }}
        >
          <Box sx={{ fontSize: "2.5rem", fontWeight: "700" }}>
            {tenant.companyName}
          </Box>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              signIn(ID, PW);
            }}
            sx={{
              width: "64mm",
              display: "flex",
              flexDirection: "column",
              margin: "64px auto",
            }}
          >
            <Box
              sx={{
                fontSize: "1.6rem",
                fontWeight: "700",
                letterSpacing: "1px",
              }}
            >
              DAON CRM
            </Box>
            <Box sx={{ width: "100%" }}>
              <CssTextField
                onKeyUp={Util.enterToFocusMove}
                sx={classes.textField_ID}
                variant="outlined"
                required
                id="ID"
                label="아이디"
                name="ID"
                onChange={(e) => handleChange(e)}
                value={ID}
                size="small"
                autoFocus
                InputProps={{
                  sx: {
                    fontSize: "15px !important",
                    width: "64mm",
                    backgroundColor: "white",
                  },
                }}
                InputLabelProps={{
                  sx: {
                    fontSize: "15px",
                    transform: "translate(14px, 12px) scale(1)",
                    "&.MuiInputLabel-shrink": {
                      transform: "translate(14px, -9px) scale(0.75)",
                    },
                  },
                }}
              />
            </Box>
            <Box>
              <CssTextField
                onKeyUp={(e) => {
                  if (e.key === "Enter") {
                    signIn(ID, PW);
                  }
                }}
                sx={classes.textField_PW}
                variant="outlined"
                required
                name="PW"
                label="비밀번호"
                type="password"
                id="PW"
                onChange={(e) => handleChange(e)}
                value={PW}
                size="small"
                InputProps={{
                  sx: {
                    fontSize: "15px !important",
                    width: "64mm",
                    backgroundColor: "white",
                  },
                }}
                InputLabelProps={{
                  sx: {
                    fontSize: "15px",
                    transform: "translate(14px, 12px) scale(1)",
                    "&.MuiInputLabel-shrink": {
                      transform: "translate(14px, -9px) scale(0.75)",
                    },
                  },
                }}
              />
            </Box>
            <Box sx={{ marginTop: "8px", marginBottom: "8px" }}>
              <Box sx={{ display: "flex", flexDirection: "row", gap: "16px" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="radio"
                    id="radio1"
                    name="radio"
                    value="1"
                    checked={regOption === "1"}
                    onChange={(e) => setRegOption(e.target.value)}
                    style={{ marginRight: "4px" }}
                  />
                  <label htmlFor="radio1" style={{ fontSize: "14px", cursor: "pointer" }}>
                    자사용
                  </label>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="radio"
                    id="radio2"
                    name="radio"
                    value="2"
                    checked={regOption === "2"}
                    onChange={(e) => setRegOption(e.target.value)}
                    style={{ marginRight: "4px" }}
                  />
                  <label htmlFor="radio2" style={{ fontSize: "14px", cursor: "pointer" }}>
                    업체용
                  </label>
                </Box>
              </Box>
            </Box>
            <Box>
              <Button
                sx={classes.button}
                onClick={() => signIn(ID, PW)}
                type="submit"
                variant="contained"
              >
                <Typography sx={{ fontSize: "1rem", fontWeight: 400 }}>
                  로 그 인
                </Typography>
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          />
        </Box>
      </Box>
      <Box
        component="img"
        src={tenant.logoPath}
        sx={{
          position: "absolute",
          top: "18px",
          left: "18px",
          width: "36mm",
          margin: "0 auto",
        }}
      />
    </Box>
  );
}
export default Login;
